import classnames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import Icon from '@wetransfer/transfer/component/icon';
import {
  PricingPanel,
  Props as PricingPanelProps,
} from '@wetransfer/transfer/component/PricingPanel/PricingPanel';
import { PricingTable } from '@wetransfer/transfer/component/PricingTable/PricingTable';
import { WHITE } from '@wetransfer/transfer/constant/colors';
import {
  FF_FREE_ACCOUNT_SPLASH_EXPERIMENT,
  EXP_FREE_ACCOUNT_SPLASH,
} from '@wetransfer/transfer/constant/experiments';

import './Content.scss';

import ExperimentSplashPageDiscountLabel from '@wetransfer/transfer/constant/experiments/splash-page-discount-label';
import ExperimentSplashPageShowPremiumCard from '@wetransfer/transfer/constant/experiments/splash-page-show-premium-card';
import ExperimentSplashShowAllTiers from '@wetransfer/transfer/constant/experiments/splash-show-all-tiers-experiment';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import { getVariant } from '@wetransfer/transfer/modules/experiments';
import I18n from '@wetransfer/transfer/modules/i18n';
import { Button } from '@wetransfer/transfer/oneui/react';
import { State } from '@wetransfer/transfer/store/types';

export interface Props {
  visible: boolean;
  title: string;
  dismissCtaText?: string;
  onDismiss(): void;
  onClickOutside?: () => void;
  className?: string;
  children: JSX.Element;
}

interface Content {
  features: PricingPanelProps['features'];
  title?: string;
  pricing?: PricingPanelProps['pricing'];
  cta?: PricingPanelProps['cta'];
  description?: PricingPanelProps['description'];
}

export interface ContentProps {
  freeContent: Content;
  proContent: Content;
  premiumContent?: Content;
}

export function SplashPageContent({
  proContent,
  freeContent,
  premiumContent,
}: ContentProps): JSX.Element {
  const { user } = useSelector((state: State) => ({
    user: state.user,
  }));

  const showAllTierCards = ExperimentSplashShowAllTiers.isEnabled();

  const showOnlyProAndPremiumTiersCards =
    getVariant(FF_FREE_ACCOUNT_SPLASH_EXPERIMENT) === 'on' ||
    getVariant(EXP_FREE_ACCOUNT_SPLASH.key, EXP_FREE_ACCOUNT_SPLASH.default) ===
      EXP_FREE_ACCOUNT_SPLASH.showPremiumCard;

  const showPremiumTierCards = ExperimentSplashPageShowPremiumCard.isEnabled();

  const showDiscountLabel =
    ExperimentSplashPageDiscountLabel.isShowSplashpageDiscountLabel();

  const showProAndPremiumCards =
    user?.planTier === 'free' && showOnlyProAndPremiumTiersCards;

  const freeCard = (
    <PricingPanel
      colorScheme="Transparent"
      title={I18n.t('page.pricing_panel.plans.free.title')}
      features={freeContent.features}
      cta={freeContent.cta}
      description={freeContent.description}
    />
  );

  const proCard = (
    <PricingPanel
      colorScheme={'Light'}
      title={proContent.title}
      features={proContent.features}
      cta={{
        ...proContent.cta,
        ...(showProAndPremiumCards && {
          href: '/sign-up?tier=plus&trk=WT202005_splashpage_free_to_pro',
        }),
      }}
      pricing={proContent.pricing}
      description={proContent.description}
    />
  );

  const DiscountProCard = (
    <PricingPanel
      additionalClass={showDiscountLabel && 'PricingPanel--experimentCard'}
      colorScheme={showDiscountLabel ? 'Medium' : 'Light'}
      title={proContent.title}
      features={proContent.features}
      cta={proContent.cta}
      pricing={proContent.pricing}
      description={proContent.description}
      showHeaderLabel={showDiscountLabel && showDiscountLabel}
      payload={ExperimentSplashPageDiscountLabel.payload}
    />
  );

  const premiumCard = (
    <PricingPanel
      colorScheme="Medium"
      title={premiumContent.title}
      features={premiumContent.features}
      cta={{
        ...premiumContent.cta,
        ...(showProAndPremiumCards && {
          href: '/sign-up?tier=premium_2022&trk=WT202005_splashpage_free_to_premium',
        }),
      }}
      description={premiumContent.description}
    />
  );

  function renderSplashPagePricingCards() {
    if (showDiscountLabel) {
      return (
        <>
          {freeCard} {DiscountProCard}
        </>
      );
    } else if (!showPremiumTierCards && !showAllTierCards) {
      return (
        <>
          {freeCard} {proCard}
        </>
      );
    } else if (showPremiumTierCards && !showAllTierCards) {
      return (
        <>
          {freeCard} {premiumCard}
        </>
      );
    } else if (showAllTierCards) {
      return (
        <>
          {freeCard} {proCard} {premiumCard}
        </>
      );
    }
  }

  function tierCardDisplay() {
    if (showProAndPremiumCards) {
      return (
        <>
          {proCard} {premiumCard}
        </>
      );
    }

    return renderSplashPagePricingCards();
  }

  return (
    <PricingTable additionalClass={showAllTierCards ? 'allCards' : null}>
      {tierCardDisplay()}
    </PricingTable>
  );
}

export default function SplashPage({
  visible,
  onDismiss,
  onClickOutside,
  className,
  title,
  children,
}: Props): JSX.Element {
  function handleDismissLogo(e: MouseEvent) {
    e.stopPropagation();
    onDismiss();

    Analytics.trackSnowplowEvent({
      category: 'splashscreen',
      action: 'click_we_logo',
    });
    Analytics.trackGoogleEvent({
      category: 'splashscreen',
      action: 'click_we_logo',
    });
  }

  function handleDismiss(e?: React.MouseEvent): void {
    e.stopPropagation();
    onDismiss();

    Analytics.trackSnowplowEvent({
      category: 'splashscreen',
      action: 'click_take_me_to_free',
    });
    Analytics.trackGoogleEvent({
      category: 'splashscreen',
      action: 'click_take_me_to_free',
    });

    trackAmplitudeEvent({
      action: 'Upsell',
      additionalProperties: {
        Context: 'Send: Splash Page',
        Type: 'Splash Page',
        Action: 'Dismiss',
      },
    });
  }

  return (
    <CSSTransition
      timeout={{ enter: 0, exit: 300 }}
      in={visible}
      unmountOnExit={true}
      classNames="splashpage__transition"
    >
      {
        // eslint-disable-next-line
        <div
          className={classnames('splashpage', className)}
          onClick={onClickOutside}
          data-testid="splashpage"
        >
          <div className="splashpage__content--container">
            <div className="splashpage__content">
              <Icon type="Logo" onClick={handleDismissLogo} color={WHITE} />
              <h1 className="splashpage__title">{title}</h1>
              {children}
              <div className="splashpage__dismiss-wrapper">
                <Button
                  onClick={handleDismiss}
                  className="splashpage__dismiss-button"
                >
                  {I18n.t('page.splashpage.cta')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      }
    </CSSTransition>
  );
}
